import { useState, useCallback } from "react";
import { NodeViewWrapper, type NodeViewProps } from "@tiptap/react";
import { BlockEditModal } from "../BlockEditModal";

interface Phone { label?: string; number: string }
interface Social { network: string; handle: string; url?: string }

export function PersonalInfoNodeView({ node, updateAttributes, selected, deleteNode }: NodeViewProps) {
  const name = (node.attrs.name as string) || "";
  const headline = (node.attrs.headline as string) || "";
  const email = (node.attrs.email as string) || "";
  const homepage = (node.attrs.homepage as string) || "";
  const location = (node.attrs.location as string) || "";
  const phones = (node.attrs.phones as Phone[]) || [];
  const socials = (node.attrs.socials as Social[]) || [];

  const [isOpen, setIsOpen] = useState(false);
  const [draftName, setDraftName] = useState(name);
  const [draftHeadline, setDraftHeadline] = useState(headline);
  const [draftEmail, setDraftEmail] = useState(email);
  const [draftPhone, setDraftPhone] = useState(phones[0]?.number || "");
  const [draftLocation, setDraftLocation] = useState(location);
  const [draftHomepage, setDraftHomepage] = useState(homepage);

  const handleOpen = useCallback(() => {
    setDraftName(name); setDraftHeadline(headline); setDraftEmail(email);
    setDraftPhone(phones[0]?.number || ""); setDraftLocation(location); setDraftHomepage(homepage);
    setIsOpen(true);
  }, [name, headline, email, phones, location, homepage]);

  const handleSave = useCallback(() => {
    updateAttributes({
      name: draftName,
      headline: draftHeadline,
      email: draftEmail,
      phones: draftPhone ? [{ number: draftPhone }] : [],
      location: draftLocation,
      homepage: draftHomepage,
    });
    setIsOpen(false);
  }, [draftName, draftHeadline, draftEmail, draftPhone, draftLocation, draftHomepage, updateAttributes]);

  // Display: contact row stitches together email · phone · location · homepage
  const contactBits = [
    email,
    ...phones.map(p => p.number).filter(Boolean),
    location,
    homepage,
  ].filter(Boolean);

  const isEmpty = !name && !headline && contactBits.length === 0;

  return (
    <NodeViewWrapper data-type="personal-info-block" data-block-id={node.attrs.blockId} className={selected ? "ProseMirror-selectednode" : ""}>
      <div onClick={handleOpen} title="Click to edit personal info" style={{
        padding: "16px 20px",
        borderRadius: 6,
        border: "1px solid var(--lilia-border, #e5e5e5)",
        background: "var(--lilia-surface, #fafafa)",
        cursor: "pointer",
        margin: "8px 0",
      }}>
        {isEmpty ? (
          <span style={{ color: "var(--lilia-text-muted, #999)", fontStyle: "italic" }}>
            Click to add personal info (name, headline, contact)
          </span>
        ) : (
          <>
            {name && <div style={{ fontSize: 22, fontWeight: 700, color: "var(--lilia-text, #111)" }}>{name}</div>}
            {headline && <div style={{ fontSize: 14, fontStyle: "italic", color: "var(--lilia-text-muted, #555)", marginTop: 2 }}>{headline}</div>}
            {contactBits.length > 0 && (
              <div style={{ fontSize: 12, color: "var(--lilia-text-muted, #555)", marginTop: 6 }}>
                {contactBits.join(" · ")}
              </div>
            )}
            {socials.length > 0 && (
              <div style={{ fontSize: 12, color: "var(--lilia-text-muted, #555)", marginTop: 4 }}>
                {socials.map(s => `${s.network}: ${s.handle}`).join(" · ")}
              </div>
            )}
          </>
        )}
      </div>

      <BlockEditModal
        sourceNode={node}
        title="Edit Personal Info"
        open={isOpen}
        onClose={() => setIsOpen(false)}
        onSave={handleSave}
        onDelete={() => { setIsOpen(false); deleteNode(); }}
        width={560}
      >
        <FieldGrid>
          <Field label="Name" value={draftName} onChange={setDraftName} placeholder="Full name" autoFocus />
          <Field label="Headline" value={draftHeadline} onChange={setDraftHeadline} placeholder="e.g. Software Engineer" />
          <Field label="Email" value={draftEmail} onChange={setDraftEmail} type="email" placeholder="you@example.com" />
          <Field label="Phone" value={draftPhone} onChange={setDraftPhone} placeholder="+1 555 0100" />
          <Field label="Location" value={draftLocation} onChange={setDraftLocation} placeholder="City, Country" />
          <Field label="Homepage" value={draftHomepage} onChange={setDraftHomepage} placeholder="https://…" />
        </FieldGrid>
      </BlockEditModal>
    </NodeViewWrapper>
  );
}

// ── shared modal field helpers (kept inline; promoted later if reused) ──

function FieldGrid({ children }: { children: React.ReactNode }) {
  return <div style={{ display: "grid", gap: 12 }}>{children}</div>;
}

function Field({ label, value, onChange, placeholder, type = "text", autoFocus = false }: {
  label: string; value: string; onChange: (v: string) => void;
  placeholder?: string; type?: string; autoFocus?: boolean;
}) {
  return (
    <div>
      <label style={{ display: "block", fontSize: 12, fontWeight: 600, marginBottom: 4 }}>{label}</label>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        autoFocus={autoFocus}
        style={{
          width: "100%", padding: "8px 10px", fontSize: 13,
          border: "1px solid var(--lilia-border, #ddd)", borderRadius: 4,
        }}
      />
    </div>
  );
}
